import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedTextNode,
    Spread,
} from 'lexical';
import { $applyNodeReplacement, TextNode } from 'lexical';
import { EVENT_NUMBER_MENTION_TRIGGER, MentionNodeType } from './constants';
export type SerializedMentionNode = Spread<
    {
        mentionName: string;
    },
    SerializedTextNode
>;

function getDataLexicalAttributeName(name: string): string {
    return `data-lexical-${name}`;
}

function convertEventNumberMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
    const textContent = domNode.textContent;

    if (textContent !== null) {
        const node = $createEventNumberMentionNode(textContent);
        return {
            node,
        };
    }

    return null;
}

const mentionStyle = `
    color: var(--arc-colors-text-primary);
    background-color: var(--arc-colors-neutral-default);
    border: 1px solid var(--arc-colors-neutral-default);
    border-radius: 5px;
    padding-left: 3px;
    padding-right: 3px;
`;
export class EventNumberMentionNode extends TextNode {
    __mention: string;

    static getType(): string {
        return MentionNodeType.EventNumber;
    }

    static clone(node: EventNumberMentionNode): EventNumberMentionNode {
        return new EventNumberMentionNode(node.__mention, node.__text, node.__key);
    }
    static importJSON(serializedNode: SerializedMentionNode): EventNumberMentionNode {
        const node = $createEventNumberMentionNode(serializedNode.mentionName);
        node.setTextContent(serializedNode.text);
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    constructor(mentionName: string, text?: string, key?: NodeKey) {
        super(text ?? mentionName, key);
        this.__mention = mentionName;
    }

    exportJSON(): SerializedMentionNode {
        return {
            ...super.exportJSON(),
            mentionName: this.__mention,
            type: MentionNodeType.EventNumber,
            version: 1,
        };
    }

    createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.style.cssText = mentionStyle;
        dom.className = MentionNodeType.EventNumber;
        return dom;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span');
        element.setAttribute(getDataLexicalAttributeName(MentionNodeType.EventNumber), 'true');
        element.textContent = this.__text;
        return { element };
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (
                    !domNode.hasAttribute(getDataLexicalAttributeName(MentionNodeType.EventNumber))
                ) {
                    return null;
                }
                return {
                    conversion: convertEventNumberMentionElement,
                    priority: 1,
                };
            },
        };
    }

    isTextEntity(): true {
        return true;
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    canInsertTextAfter(): boolean {
        return false;
    }
}

export function $createEventNumberMentionNode(mentionName: string): EventNumberMentionNode {
    const mentionNode = new EventNumberMentionNode(`${EVENT_NUMBER_MENTION_TRIGGER}${mentionName}`);
    mentionNode.setMode('segmented').toggleDirectionless();
    return $applyNodeReplacement(mentionNode);
}

export function $isEventNumberMentionNode(
    node: LexicalNode | null | undefined
): node is EventNumberMentionNode {
    return node instanceof EventNumberMentionNode;
}

export const MentionNodes = [EventNumberMentionNode];
